// Access type means if user has access to the app (NONE means no access, GROWTHMENTOR means access to growthmentor app)
export const ACCESS_TYPE = {
  NONE: 'NONE',
  GROWTHMENTOR: 'GROWTHMENTOR',
} as const

// Current "user mode" (only mentor can switch between mentee/mentor, and mentee is always mentee)
export const ACTOR_TYPE = {
  NONE: 'NONE',
  MENTEE: 'MENTEE',
  MENTOR: 'MENTOR',
} as const

// Mentor profile status
export const MENTOR_STATUS = {
  ACTIVE: 'ACTIVE',
  HIDDEN_FROM_AUTO_DECLINE: 'HIDDEN_FROM_AUTO_DECLINE',
  HIDDEN: 'HIDDEN',
  PROFILE_INCOMPLETE: 'PROFILE_INCOMPLETE',
  NOT_MENTOR: 'NOT_MENTOR',
} as const

// Each user must complete their profile before using some features
export const PROFILE_STATUS = {
  NOT_COMPLETE: 'NOT_COMPLETE',
  COMPLETE: 'COMPLETE',
} as const

export const PROFILE_VISIBILITY = {
  VISIBLE: 'VISIBLE',
  INVISIBLE: 'INVISIBLE',
} as const

// Base user role (mentee is always MENTEE, mentor is always MENTOR not matter what mode they are in, GUEST is not logged-in user)
export const ROLE = {
  GUEST: 'GUEST',
  MENTEE: 'MENTEE',
  MENTOR: 'MENTOR',
} as const

export const TEAM_ROLE = {
  NONE: 'NONE',
  INVITED: 'INVITED',
  MEMBER: 'MEMBER',
  OWNER: 'OWNER',
} as const

export const SUBSCRIPTION_BILLING_CYCLE = {
  MONTH: 'MONTH',
  QUARTER: 'QUARTER',
  YEAR: 'YEAR',
} as const

export const SUBSCRIPTION_NEXT_BILLING_PERIOD_STATUS = {
  // NONE means that subscription is not active and will not be
  NONE: 'NONE',
  // EXPIRED means that subscription is not active and will not be, but it was active in the past
  EXPIRED: 'EXPIRED',
  // ACTIVE means that subscription is active and will be active
  ACTIVE: 'ACTIVE',
  // CANCELED means that subscription is active, but it will not be active
  CANCELED: 'CANCELED',
  // SCHEDULED means that subscription is active, but new subscription will be active (maybe someone upgrades plan)
  SCHEDULED: 'SCHEDULED',
} as const

export const SUBSCRIPTION_STATUS = {
  // User physically does NOT pay for subscription with real money
  NOT_PAID: 'NOT_PAID',
  // User physically pays for subscription with real money
  PAID: 'PAID',
} as const

// This status should only be checked for mentors (they are the only ones that can connect to stripe)
export const SUBSCRIPTION_STRIPE_CONNECTION_STATUS = {
  // User is not connected to stripe
  NOT_ACTIVE: 'NOT_ACTIVE',
  // User is connected to stripe
  ACTIVE: 'ACTIVE',
} as const

export const SUBSCRIPTION_TIER = {
  // User does not have any subscription
  NONE: 'NONE',
  // User has a "trial" subscription (available through checkbox in admin panel)
  TRIAL: 'TRIAL',
  // User has a "baby" subscription (it is subscription with lower price that cannot be bought by user directly, only if user cancels subscription)
  BABY: 'BABY',
  // User has a "basic" subscription (it is each subscription that can be bought by user directly in plans page or in checkout page)
  USER: 'USER',
  // User has a "team" subscription
  TEAM: 'TEAM',
  // User has a "permanent" subscription (available through checkbox in admin panel)
  PERMANENT: 'PERMANENT',
} as const

export const SUBSCRIPTION_TYPE = {
  NONE: 'NONE',
  // User has legacy subscription that cannot be bought in any way
  LEGACY: 'LEGACY',
  LIVE: 'LIVE',
} as const

export const SUBSCRIPTION_GROUP = {
  BASIC: 'BASIC',
  LIGHT: 'LIGHT',
  PRO: 'PRO',
  HIBERNATION: 'HIBERNATION',
} as const

export const SUBSCRIPTION_TRIAL_INTERVAL = {
  NONE: 'NONE',
  ONCE: 'ONCE',
  MONTHLY: 'MONTHLY',
} as const

export const OBJECT_PERMISSIONS = {
  COLLECTIONS: {
    NOT_FOLLOWING: 'NOT_FOLLOWING',
    FOLLOWING: 'FOLLOWING',
    APPLIED: 'APPLIED',
    NOT_APPLIED: 'NOT_APPLIED',
    REQUESTED: 'REQUESTED',
    DECLINED: 'DECLINED',
  },
  CONNECTION_STATUS: {
    ACCEPTED: 'ACCEPTED',
    NOT_CONNECTED: 'NOT_CONNECTED',
  },
  GROUPS: {
    NOT_APPLIED: 'NOT_APPLIED',
    APPROVED: 'APPROVED',
    REQUESTED: 'REQUESTED',
  },
  OPPORTUNITIES: {
    NOT_APPLIED: 'NOT_APPLIED',
    OWNER: 'OWNER',
    VIEWER: 'VIEWER',
    LIKE: 'LIKE',
  },
  MENTORS: {
    OWNER: 'OWNER',
    VIEWER: 'VIEWER',
    PROVIDES_COACHING: 'PROVIDES_COACHING',
  },
  MENTEES: {
    OWNER: 'OWNER',
    VIEWER: 'VIEWER',
  },
  SESSIONS: {
    MENTEE: 'MENTEE',
    MENTOR: 'MENTOR',
  },
  REVIEWS: {
    FAVORITED: 'FAVORITED',
  },
} as const

// Type exports
export type AccessType = (typeof ACCESS_TYPE)[keyof typeof ACCESS_TYPE]
export type ActorType = (typeof ACTOR_TYPE)[keyof typeof ACTOR_TYPE]
export type MentorStatus = (typeof MENTOR_STATUS)[keyof typeof MENTOR_STATUS]
export type ProfileStatus = (typeof PROFILE_STATUS)[keyof typeof PROFILE_STATUS]
export type ProfileVisibility =
  (typeof PROFILE_VISIBILITY)[keyof typeof PROFILE_VISIBILITY]
export type Role = (typeof ROLE)[keyof typeof ROLE]
export type TeamRole = (typeof TEAM_ROLE)[keyof typeof TEAM_ROLE]
export type SubscriptionBillingCycle =
  (typeof SUBSCRIPTION_BILLING_CYCLE)[keyof typeof SUBSCRIPTION_BILLING_CYCLE]
export type SubscriptionNextBillingPeriodStatus =
  (typeof SUBSCRIPTION_NEXT_BILLING_PERIOD_STATUS)[keyof typeof SUBSCRIPTION_NEXT_BILLING_PERIOD_STATUS]
export type SubscriptionStatus =
  (typeof SUBSCRIPTION_STATUS)[keyof typeof SUBSCRIPTION_STATUS]
export type SubscriptionStripeConnectionStatus =
  (typeof SUBSCRIPTION_STRIPE_CONNECTION_STATUS)[keyof typeof SUBSCRIPTION_STRIPE_CONNECTION_STATUS]
export type SubscriptionTier =
  (typeof SUBSCRIPTION_TIER)[keyof typeof SUBSCRIPTION_TIER]
export type SubscriptionType =
  (typeof SUBSCRIPTION_TYPE)[keyof typeof SUBSCRIPTION_TYPE]
export type SubscriptionGroup =
  (typeof SUBSCRIPTION_GROUP)[keyof typeof SUBSCRIPTION_GROUP]
export type SubscriptionTrialInterval =
  (typeof SUBSCRIPTION_TRIAL_INTERVAL)[keyof typeof SUBSCRIPTION_TRIAL_INTERVAL]
